import type { SectionInstance } from '@aquilixshop/ui-react';

/**
 * SECTION ZONES — the one place that says what is site-wide CHROME and what is page TEMPLATE.
 *
 * The rail presents the page as three groups (HEADER · TEMPLATE · FOOTER) built by FILTERING `order` by
 * section type, so the rail always draws the header group first no matter where the header actually sits
 * in `order`. That presentation is only honest if the document can't interleave chrome with the body —
 * hence these helpers, used by:
 *   - `orderWithChromePinned` — normalizes a loaded document (self-heals a page whose chrome drifted),
 *   - `clampToZone` — a move can never leave its own zone (the canvas had no such rule; the rail's
 *     per-group DndContext already had it),
 *   - `SectionRail` — the group partition itself.
 *
 * Why it matters (bug 2026-08-03): a HIDDEN section renders as a zero-height node in the canvas, so a
 * neighbouring section dragged a few pixels over it swapped past it with NO visible change — the header
 * silently sank into the middle of the page. The rail kept showing it under HEADER (it filters by type),
 * so nothing hinted at the drift until the seller un-hid it, or added an announcement bar "to the header"
 * and watched it land mid-page.
 */
export const HEADER_SECTION_TYPES = ['horizontal-ticker', 'announcement-bar', 'header'];
export const FOOTER_SECTION_TYPES = ['footer'];
export const CHROME_SECTION_TYPES = [...HEADER_SECTION_TYPES, ...FOOTER_SECTION_TYPES];

export type SectionZone = 'header' | 'template' | 'footer';

/** Which rail group a section type belongs to. Unknown types are body content. */
export function zoneOfType(type: string): SectionZone {
  if (HEADER_SECTION_TYPES.includes(type)) return 'header';
  if (FOOTER_SECTION_TYPES.includes(type)) return 'footer';
  return 'template';
}

/**
 * The section ids in the ONE order the rail claims: header chrome, then the template body, then the
 * footer — each group keeping its own relative order. A healthy document comes back unchanged; a drifted
 * one is repaired, which is why this runs on every load path (fetch AND page-cache entry).
 */
export function orderWithChromePinned(sections: SectionInstance[]): string[] {
  const header: string[] = [];
  const template: string[] = [];
  const footer: string[] = [];
  for (const sec of sections) {
    const bucket =
      zoneOfType(sec.type) === 'header'
        ? header
        : zoneOfType(sec.type) === 'footer'
          ? footer
          : template;
    bucket.push(sec.id);
  }
  return [...header, ...template, ...footer];
}

/**
 * Clamp a move target so a section stays inside its own zone: the allowed landing slots are the indices
 * its zone already occupies. A template section dropped "above the page" therefore lands under the header
 * instead of on top of it — including when the header is HIDDEN and thus invisible in the canvas.
 */
export function clampToZone(
  order: string[],
  sectionsById: Record<string, SectionInstance>,
  id: string,
  toIndex: number,
): number {
  const zone = zoneOfType(sectionsById[id]?.type ?? '');
  let lo = -1;
  let hi = -1;
  order.forEach((sid, i) => {
    if (zoneOfType(sectionsById[sid]?.type ?? '') !== zone) return;
    if (lo === -1) lo = i;
    hi = i;
  });
  if (lo === -1) return toIndex; // shouldn't happen (the section itself is in its zone)
  return Math.max(lo, Math.min(toIndex, hi));
}
